import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { assignmentAPI } from '../../api/endpoints';
import { ChevronDown, Building2, Calendar, Users } from 'lucide-react';

const EventSelector = ({ selectedEvent, onEventChange, className = '', attendeeCount = 0, eventsWithCounts = [] }: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ['user-events'],
    queryFn: async () => {
      const response = await assignmentAPI.getUserEvents();
      return response.data;
    },
  });

  const events = useMemo(() => eventsData?.events || [], [eventsData]);
  const currentEvent = events.find(event => event.id === selectedEvent);
  
  // Helper function to get real attendee count for an event
  const getRealAttendeeCount = (eventId: string) => {
    // First try realAttendeeCount from explicitly passed eventsWithCounts
    const explicitEvent = eventsWithCounts.find((e: any) => e.id === eventId);
    if (explicitEvent && explicitEvent.realAttendeeCount !== undefined) {
      return explicitEvent.realAttendeeCount;
    }
    
    // Otherwise fallback to attendees_count from the internally fetched events
    const fetchedEvent = events.find((e: any) => e.id === eventId);
    return fetchedEvent?.attendees_count || explicitEvent?.attendees_count || 0;
  };

  // Auto-select first event if none selected
  useEffect(() => {
    if (events.length > 0 && !selectedEvent) {
      onEventChange(events[0].id);
    }
  }, [events, selectedEvent, onEventChange]);

  if (isLoading) {
    return (
      <div className={`animate-pulse bg-gray-200 rounded-lg h-12 w-64 ${className}`} />
    );
  }

  if (events.length === 0) {
    return (
      <div className={`text-gray-500 text-sm ${className}`}>
        No events assigned
      </div>
    );
  }

  if (events.length === 1) {
    return (
      <div className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white rounded-lg border ${className}`}>
        <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-primary-100 rounded-lg flex-shrink-0">
          <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate text-sm sm:text-base">{currentEvent?.name}</h3>
          <p className="text-xs sm:text-sm text-gray-500 truncate">
            {getRealAttendeeCount(currentEvent?.id)} attendees
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-white rounded-lg border hover:border-primary-300 transition-colors"
      >
        <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-primary-100 rounded-lg flex-shrink-0">
          <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-primary-600" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <h3 className="font-semibold text-gray-900 truncate text-sm sm:text-base">
            {currentEvent?.name || 'Select Event'}
          </h3>
          <p className="text-xs sm:text-sm text-gray-500 truncate">
            {currentEvent ? `${getRealAttendeeCount(currentEvent.id)} attendees` : 'Choose an event to manage'}
          </p>
        </div>
        <ChevronDown className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown - Mobile Responsive with Better Scrolling */}
          <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg border shadow-lg z-20 
                          max-h-[60vh] md:max-h-80 overflow-y-auto overflow-x-hidden
                          scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {events.map((event) => (
              <button
                key={event.id}
                onClick={() => {
                  onEventChange(event.id);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2 sm:gap-3 p-2 sm:p-3 hover:bg-gray-50 transition-colors ${
                  event.id === selectedEvent ? 'bg-primary-50 border-r-2 border-primary-500' : ''
                }`}
              >
                <div className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex-shrink-0 ${
                  event.id === selectedEvent ? 'bg-primary-100' : 'bg-gray-100'
                }`}>
                  <Calendar className={`h-4 w-4 sm:h-5 sm:w-5 ${
                    event.id === selectedEvent ? 'text-primary-600' : 'text-gray-600'
                  }`} />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <h3 className={`font-semibold truncate text-sm sm:text-base ${
                    event.id === selectedEvent ? 'text-primary-900' : 'text-gray-900'
                  }`}>
                    {event.name}
                  </h3>
                  <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {getRealAttendeeCount(event.id)}
                    </span>
                    {event.location && (
                      <span className="flex items-center gap-1 truncate">
                        <Building2 className="h-3 w-3" />
                        <span className="truncate">{event.location}</span>
                      </span>
                    )}
                  </div>
                </div>
                {event.user_assignment && (
                  <div className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full flex-shrink-0 hidden sm:block">
                    {event.user_assignment.role}
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default EventSelector;
